/**
 * Money amounts are typed with thousands separators ("28,463,000") so a
 * missing or extra zero is easy to spot; percentages, years and ratios are
 * left as plain numbers.
 */
export const AMOUNT_FIELDS = new Set([
  "loan_amount_day_one",
  "loan_amount_fully_funded",
  "facility_agent_fee",
  "equity_contribution",
  "amortisation_amount",
]);

/**
 * Tidy what was typed into "-1,234,567.89" form: keeps digits, one decimal
 * point and a leading minus, and groups the whole part in threes.
 */
export function formatGrouped(raw: string): string {
  const negative = raw.trim().startsWith("-");
  const cleaned = raw.replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  const whole = (dot === -1 ? cleaned : cleaned.slice(0, dot)).replace(
    /^0+(?=\d)/,
    "",
  );
  const fraction =
    dot === -1 ? null : cleaned.slice(dot + 1).replace(/\./g, "");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}${grouped}${fraction === null ? "" : `.${fraction}`}`;
}

/** "28,463,000" -> 28463000; "" or nonsense -> null. */
export function parseGrouped(text: string): number | null {
  const plain = text.replace(/,/g, "").trim();
  if (plain === "" || plain === "-" || plain === ".") return null;
  const n = Number(plain);
  return Number.isFinite(n) ? n : null;
}

/** A stored number shown with separators ("" for empty). */
export function groupedFromValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  return formatGrouped(String(value));
}

/**
 * Where the caret goes after re-formatting: just after the same number of
 * digits (and decimal point) as were before it, so adding separators while
 * typing never makes the caret jump.
 */
export function caretAfterFormat(
  typed: string,
  caret: number,
  formatted: string,
): number {
  const significant = typed.slice(0, caret).replace(/[^\d.-]/g, "").length;
  if (significant === 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/[\d.-]/.test(formatted[i])) seen += 1;
    if (seen === significant) return i + 1;
  }
  return formatted.length;
}
